import { useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useI18n } from "@/context/I18nContext";
import { ArrowDownUp, ArrowUp, ArrowDown, ArrowLeft } from "lucide-react";
import { ACTIONS } from "./actions";

function SortIndicator({ active, dir }) {
  if (!active) return <ArrowDownUp className="h-3 w-3 text-muted-foreground/60" />;
  return dir === "asc" ? (
    <ArrowUp className="h-3 w-3 text-primary" />
  ) : (
    <ArrowDown className="h-3 w-3 text-primary" />
  );
}

export function ReviewStep({ action, params, rows, selectedRowIds, setSelectedRowIds, onBack, onUpdate }) {
  const { t } = useI18n();
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState({ col: "code", dir: "asc" });

  const def = ACTIONS[action];
  const fieldLabel = t(def.fieldLabelKey(params));

  // Decorate raw rows (built in the parent from ACTIONS[action].resolve) with
  // display labels + the changing/issue flags the filter & selection rules key off.
  const decorated = useMemo(
    () =>
      rows.map((r) => {
        const issue = r.notFound || r.duplicate || r.na;
        const changing = !issue && r.current !== r.next;
        const currentLabel = r.notFound ? "—" : def.formatValue(t, r.current);
        const newLabel = r.notFound ? "—" : r.na ? t("bulkUpdate.review.na") : def.formatValue(t, r.next);
        return { ...r, issue, changing, currentLabel, newLabel };
      }),
    [rows, def, t],
  );

  const filtered = useMemo(() => {
    if (filter === "changing") return decorated.filter((r) => r.changing);
    if (filter === "issues") return decorated.filter((r) => r.issue);
    return decorated;
  }, [decorated, filter]);

  const sorted = useMemo(() => {
    const dir = sort.dir === "asc" ? 1 : -1;
    const key = sort.col;
    const val = (r) => {
      if (key === "code") return r.code;
      if (key === "name") return r.dealer?.name || "";
      if (key === "current") return r.currentLabel;
      if (key === "new") return r.newLabel;
      return "";
    };
    return [...filtered].sort(
      (a, b) => String(val(a)).localeCompare(String(val(b)), undefined, { numeric: true }) * dir,
    );
  }, [filtered, sort]);

  const toggleSort = (col) => {
    setSort((s) => (s.col === col ? { col, dir: s.dir === "asc" ? "desc" : "asc" } : { col, dir: "asc" }));
  };

  const selectableRows = sorted.filter((r) => !r.notFound && !r.duplicate && !r.na);
  const allSelected = selectableRows.length > 0 && selectableRows.every((r) => selectedRowIds.has(r.rowId));
  const someSelected = selectableRows.some((r) => selectedRowIds.has(r.rowId)) && !allSelected;

  const toggleSelectAll = () => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (allSelected) selectableRows.forEach((r) => next.delete(r.rowId));
      else selectableRows.forEach((r) => next.add(r.rowId));
      return next;
    });
  };

  const toggleOne = (rowId) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };

  const selectedCount = rows.filter((r) => selectedRowIds.has(r.rowId)).length;

  const columns = [
    { key: "code", label: t("bulkUpdate.review.columns.dealerCode") },
    { key: "name", label: t("bulkUpdate.review.columns.dealerName") },
    { key: "current", label: t("bulkUpdate.review.columns.current", fieldLabel) },
    { key: "new", label: t("bulkUpdate.review.columns.newValue", fieldLabel) },
  ];

  return (
    <div className="flex flex-1 flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">{t("bulkUpdate.review.title")}</h2>
          <p className="text-xs text-muted-foreground" data-testid="bulk-update-review-count">
            {t("bulkUpdate.review.resultsCount", rows.length)}
            {selectedCount > 0 && <> · {t("bulkUpdate.review.selectedCount", selectedCount)}</>}
          </p>
        </div>
        <div className="w-64">
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger data-testid="bulk-update-review-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("bulkUpdate.review.filterAll")}</SelectItem>
              <SelectItem value="changing">{t("bulkUpdate.review.filterChanging")}</SelectItem>
              <SelectItem value="issues">{t("bulkUpdate.review.filterIssues")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <Card className="flex flex-1 flex-col overflow-hidden p-0">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm" data-testid="bulk-update-review-table">
            <thead className="thead">
              <tr>
                <th className="w-10 px-3 py-2">
                  <Checkbox
                    checked={allSelected}
                    onCheckedChange={toggleSelectAll}
                    data-testid="bulk-update-select-all"
                    aria-label="Select all"
                  />
                  {someSelected && <span className="ml-1 inline-block h-1 w-1 rounded-full bg-primary align-middle" />}
                </th>
                {columns.map((col) => (
                  <th key={col.key} className="whitespace-nowrap px-4 py-2 text-left">
                    <button
                      onClick={() => toggleSort(col.key)}
                      className="inline-flex items-center gap-1 hover:text-foreground"
                      data-testid={`bulk-update-sort-${col.key}`}
                    >
                      {col.label}
                      <SortIndicator active={sort.col === col.key} dir={sort.dir} />
                    </button>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.length === 0 && (
                <tr>
                  <td colSpan={columns.length + 1} className="px-6 py-16 text-center text-sm text-muted-foreground">
                    {t("bulkUpdate.review.noRows")}
                  </td>
                </tr>
              )}
              {sorted.map((r) => {
                const canSelect = !r.notFound && !r.duplicate && !r.na;
                return (
                  <tr key={r.rowId} className="border-t border-border" data-testid={`bulk-update-row-${r.rowId}`}>
                    <td className="w-10 px-3 py-2">
                      {canSelect ? (
                        <Checkbox
                          checked={selectedRowIds.has(r.rowId)}
                          onCheckedChange={() => toggleOne(r.rowId)}
                          data-testid={`bulk-update-row-select-${r.rowId}`}
                        />
                      ) : r.na ? (
                        <Checkbox checked={false} disabled data-testid={`bulk-update-row-select-${r.rowId}`} />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="whitespace-nowrap px-4 py-2 font-mono text-xs tabular-nums">{r.code}</td>
                    <td className="whitespace-nowrap px-4 py-2">
                      {r.notFound ? (
                        <span className="text-destructive">{t("bulkUpdate.review.notFound")}</span>
                      ) : (
                        <>
                          {r.dealer.name}
                          {r.duplicate && (
                            <span className="ml-2 text-xs text-muted-foreground">
                              ({t("bulkUpdate.review.duplicate")})
                            </span>
                          )}
                        </>
                      )}
                    </td>
                    <td className="px-4 py-2">{r.currentLabel}</td>
                    <td className="px-4 py-2">
                      {r.newLabel}
                      {r.na && (
                        <span className="ml-2 text-xs text-muted-foreground">
                          ({t(r.naReasonKey || "bulkUpdate.review.notApplicable")})
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-1.5" data-testid="bulk-update-back">
          <ArrowLeft className="h-4 w-4" />
          {t("bulkUpdate.back")}
        </Button>
        <Button disabled={selectedCount === 0} onClick={onUpdate} data-testid="bulk-update-submit">
          {t("bulkUpdate.review.update")}
        </Button>
      </div>
    </div>
  );
}
